import { useEffect, useState } from "react";
import type { Field, Refs, Row } from "../types";
import { IconClose } from "../icons";
import SkillsInput from "./SkillsInput";
import { ordenarPorNome } from "../ordem";

interface Props {
  title: string;
  fields: Field[];
  initial?: Row | null;
  refs: Refs; // listas para selects com ref (colaboradores / obras)
  /** Aba espelhada do RH/DP: campos com `origemRh` ficam só de leitura. */
  bloquearOrigemRh?: boolean;
  /** Registro inteiro só de leitura (não há o que salvar) — esconde "Salvar". */
  somenteLeitura?: boolean;
  onCancel: () => void;
  onSubmit: (data: Row) => Promise<void>;
}

/** Modal de cadastro/edição gerado a partir da definição de campos. */
export default function RecordForm({
  title,
  fields,
  initial,
  refs,
  bloquearOrigemRh,
  somenteLeitura,
  onCancel,
  onSubmit,
}: Props) {
  const [form, setForm] = useState<Row>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Campos calculados no banco (ex.: saldo, data_limite, situação) não são editáveis.
  const editable = fields.filter((f) => !f.computed);

  useEffect(() => {
    const base: Row = {};
    for (const f of editable) {
      let v = initial ? initial[f.name] : "";
      if (f.type === "date" && v) v = String(v).slice(0, 10); // YYYY-MM-DD
      base[f.name] = v ?? "";
    }
    setForm(base);
    setError(null);
  }, [initial, fields]);

  const set = (name: string, value: unknown) =>
    setForm((prev) => ({ ...prev, [name]: value }));

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await onSubmit(form);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onMouseDown={onCancel}>
      <div className="modal-card" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>{title}</h3>
          <button type="button" className="icon-btn" onClick={onCancel}>
            <IconClose size={18} />
          </button>
        </div>

        {/* O rodapé fica FORA do form (preso embaixo, sem entrar na rolagem):
            o botão Salvar continua enviando este form pelo atributo `form`. */}
        <form id="record-form" className="modal-body" onSubmit={submit}>
          <div className="form-grid">
            {editable.map((f) => {
              // Campo mantido pelo RH/DP (fonte única): exibido, mas não editável.
              const travado = Boolean(bloquearOrigemRh && f.origemRh);
              // Competências por marcação: é uma grade, então usa <div>
              // (um <label> só envolveria a grade inteira).
              if (f.type === "skills") {
                return (
                  <div key={f.name} className="field field-wide">
                    <span>{f.label}</span>
                    <SkillsInput
                      value={String(form[f.name] ?? "")}
                      disabled={travado}
                      onChange={(next) => set(f.name, next)}
                    />
                  </div>
                );
              }
              return (
                <label
                  key={f.name}
                  className={f.type === "textarea" ? "field field-wide" : "field"}
                >
                  <span>
                    {f.label}
                    {f.required && !travado && <em className="req"> *</em>}
                    {travado && <em className="campo-rh"> · RH/DP</em>}
                  </span>
                  {renderInput(f, form[f.name], set, refs, travado)}
                </label>
              );
            })}
          </div>

        </form>

        <div className="modal-foot">
          {error && <div className="form-error">{error}</div>}
          <button type="button" className="btn-secondary" onClick={onCancel}>
            {somenteLeitura ? "Fechar" : "Cancelar"}
          </button>
          {!somenteLeitura && (
            <button type="submit" form="record-form" className="btn-primary" disabled={saving}>
              {saving ? "Salvando..." : "Salvar"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function renderInput(
  f: Field,
  value: unknown,
  set: (name: string, value: unknown) => void,
  refs: Refs,
  /** Campo mantido por outro sistema (RH/DP): exibido, mas não editável. */
  travado = false,
) {
  const v = (value ?? "") as string | number;
  // Campo travado não é obrigatório aqui (o valor vem pronto da fonte).
  const req = f.required && !travado;

  if (f.ref) {
    // Todo select de cadastro (responsável, obra...) em ordem alfabética — é o
    // componente compartilhado, então ordenar aqui vale para todas as abas.
    const list = ordenarPorNome(refs[f.ref], (item) => item.nome);
    return (
      <select
        value={v}
        required={req}
        disabled={travado}
        onChange={(e) => set(f.name, e.target.value)}
      >
        <option value="">Selecione...</option>
        {list.map((item) => (
          <option key={item.id} value={item.id}>
            {item.nome}
          </option>
        ))}
      </select>
    );
  }

  switch (f.type) {
    case "select":
    case "status":
      return (
        <select
          value={v}
          required={req}
          disabled={travado}
          onChange={(e) => set(f.name, e.target.value)}
        >
          <option value="">Selecione...</option>
          {f.options?.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      );
    case "textarea":
      return (
        <textarea
          rows={3}
          value={v}
          disabled={travado}
          onChange={(e) => set(f.name, e.target.value)}
        />
      );
    case "number":
      return (
        <input
          type="number"
          step="any"
          value={v}
          required={req}
          disabled={travado}
          onChange={(e) => set(f.name, e.target.value)}
        />
      );
    case "currency":
      return (
        <input
          type="number"
          step="0.01"
          min="0"
          value={v}
          required={req}
          disabled={travado}
          onChange={(e) => set(f.name, e.target.value)}
        />
      );
    case "date":
      return (
        <input
          type="date"
          value={v}
          required={req}
          disabled={travado}
          onChange={(e) => set(f.name, e.target.value)}
        />
      );
    case "email":
      return (
        <input
          type="email"
          value={v}
          required={req}
          disabled={travado}
          onChange={(e) => set(f.name, e.target.value)}
        />
      );
    case "password":
      return (
        <input
          type="password"
          value={v}
          required={req}
          disabled={travado}
          placeholder={f.placeholder}
          autoComplete="new-password"
          onChange={(e) => set(f.name, e.target.value)}
        />
      );
    default:
      return (
        <input
          type="text"
          value={v}
          required={req}
          disabled={travado}
          onChange={(e) => set(f.name, e.target.value)}
        />
      );
  }
}
